import {IssueOpenedIcon, PlusCircleIcon, TriangleDownIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Box, ButtonGroup, IconButton} from '@primer/react'
import {useCallback, useRef, useState} from 'react'
import {addSubIssueMutation} from '../mutations/add-sub-issue-mutation'
import {graphql, useFragment, useRelayEnvironment} from 'react-relay'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import type {addSubIssueMutation$data} from '../mutations/__generated__/addSubIssueMutation.graphql'
import {SubIssuesCreateDialog} from './SubIssuesCreateDialog'
import type {AddSubIssueButtonGroup$key} from './__generated__/AddSubIssueButtonGroup.graphql'
import {CommandActionListItem, GlobalCommands, CommandButton} from '@github-ui/ui-commands'
import {RepositoryAndIssuePicker} from './RepositoryAndIssuePicker'
import type {IssuePickerIssue$data} from '@github-ui/item-picker/IssuePickerIssue.graphql'

export function AddSubIssueButtonGroup({
  issue,
  onCreateSubIssueSuccess,
}: {
  issue: AddSubIssueButtonGroup$key
  onCreateSubIssueSuccess?: () => void
}) {
  const [open, setOpen] = useState(false)
  const [pickerType, setPickerType] = useState<'Issue' | 'Repository' | null>(null)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const {addToast} = useToastContext()
  const environment = useRelayEnvironment()
  const anchorRef = useRef(null)

  const data = useFragment(
    graphql`
      fragment AddSubIssueButtonGroup on Issue {
        id
        repository {
          nameWithOwner
          owner {
            login
          }
        }
        ...SubIssuesCreateDialog
      }
    `,
    issue,
  )

  type AddSubIssueParams = {
    subIssue: IssuePickerIssue$data
    onComplete?: (response: addSubIssueMutation$data) => void
    onError?: (error: Error) => void
  }

  const addSubIssue = useCallback(
    ({subIssue, onComplete, onError}: AddSubIssueParams) => {
      addSubIssueMutation({
        environment,
        input: {
          issueId: data.id,
          subIssueId: subIssue.id,
          replaceParent: true,
        },
        onError: error => {
          onError?.(error)
        },
        onCompleted: response => {
          onComplete?.(response)
        },
      })
    },
    [data.id, environment],
  )

  const onIssueSelection = useCallback(
    (issues: IssuePickerIssue$data[]) => {
      // At this time, only single issue selection is supported
      const [subIssue] = issues
      if (!subIssue) return

      addSubIssue({
        subIssue,
        onError: ({message}) => {
          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          addToast({
            type: 'error',
            message,
          })
        },
      })
    },
    [addSubIssue, addToast],
  )

  const onPickerTypeChange = useCallback((t: 'Issue' | 'Repository' | null) => setPickerType(t), [setPickerType])

  return (
    <Box sx={{gap: 2, display: 'flex'}}>
      <GlobalCommands
        commands={{
          'sub-issues:add-existing-issue': () => setPickerType('Issue'),
          'sub-issues:create-sub-issue': () => setCreateDialogOpen(true),
        }}
      />
      <ButtonGroup>
        <CommandButton size="small" commandId="sub-issues:create-sub-issue">
          Create sub-issue
        </CommandButton>
        <IconButton
          size="small"
          icon={TriangleDownIcon}
          aria-label="View more sub-issue options"
          onClick={() => {
            setOpen(true)
          }}
          ref={anchorRef}
        />
      </ButtonGroup>
      <ActionMenu open={open} onOpenChange={o => setOpen(o)} anchorRef={anchorRef}>
        <ActionMenu.Overlay width="small">
          <ActionList>
            <ActionList.Group>
              <CommandActionListItem
                role="button"
                commandId="sub-issues:create-sub-issue"
                leadingVisual={<PlusCircleIcon />}
              >
                Create sub-issue
              </CommandActionListItem>
              <CommandActionListItem
                commandId="sub-issues:add-existing-issue"
                leadingVisual={<IssueOpenedIcon />}
                role="button"
              >
                Add existing issue
              </CommandActionListItem>
            </ActionList.Group>
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
      <RepositoryAndIssuePicker
        onPickerTypeChange={onPickerTypeChange}
        onIssueSelection={onIssueSelection}
        defaultRepositoryNameWithOwner={data.repository.nameWithOwner}
        organization={data.repository.owner.login}
        pickerType={pickerType}
        anchorElement={props => {
          const {ref} = props as React.HTMLAttributes<HTMLButtonElement> & {
            ref: React.MutableRefObject<HTMLButtonElement | null>
          }
          if (ref) {
            ref.current = anchorRef.current
          }
          // We don't actually want to render any element, as we are relying on the rendered section header above
          return <></>
        }}
      />
      <SubIssuesCreateDialog
        open={createDialogOpen}
        setOpen={setCreateDialogOpen}
        issue={data}
        onCreateSuccess={({createMore}) => {
          if (!createMore) {
            setCreateDialogOpen(false)
          }
          onCreateSubIssueSuccess?.()
        }}
      />
    </Box>
  )
}

try{ AddSubIssueButtonGroup.displayName ||= 'AddSubIssueButtonGroup' } catch {}